/**
 * Card Validation Utilities
 * Provides functions for validating credit card inputs
 */

interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validate credit card number using Luhn algorithm
 */
export const validateCardNumber = (cardNumber: string): ValidationResult => {
  // Remove spaces and non-digits
  const number = cardNumber.replace(/\s/g, '').replace(/\D/g, '')

  if (!number) {
    return { isValid: false, error: 'Card number is required' }
  }

  if (number.length < 13 || number.length > 19) {
    return { isValid: false, error: 'Card number must be 13-19 digits' }
  }

  // Luhn algorithm validation
  if (!luhnCheck(number)) {
    return { isValid: false, error: 'Invalid card number' }
  }

  // Card type specific validation
  const cardType = getCardType(number)
  if (!validateCardTypeLength(number, cardType)) {
    return { isValid: false, error: 'Invalid card number for card type' }
  }

  return { isValid: true }
}

/**
 * Validate card expiry date
 */
export const validateCardExpiry = (expiry: string): ValidationResult => {
  if (!expiry) {
    return { isValid: false, error: 'Expiry date is required' }
  }

  const parts = expiry.split('/')
  if (parts.length !== 2) {
    return { isValid: false, error: 'Expiry date format should be MM/YYYY' }
  }

  const [month, year] = parts
  const monthNum = parseInt(month, 10)
  let yearNum = parseInt(year, 10)

  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return { isValid: false, error: 'Invalid month' }
  }

  // Expiry is entered as MM/YY — normalize a 2-digit year to its full form.
  if (year.trim().length <= 2 && !isNaN(yearNum)) {
    yearNum += 2000
  }

  if (isNaN(yearNum) || yearNum < new Date().getFullYear()) {
    return { isValid: false, error: 'Card has expired' }
  }

  // Check if card expires this year but in a past month
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  if (yearNum === currentYear && monthNum < currentMonth) {
    return { isValid: false, error: 'Card has expired' }
  }

  return { isValid: true }
}

/**
 * Validate CVV/CVC code
 */
export const validateCardCvc = (cvc: string, cardBrand: string = ''): ValidationResult => {
  if (!cvc) {
    return { isValid: false, error: 'Security code is required' }
  }

  const cleanCvc = cvc.replace(/\D/g, '')

  if (cardBrand === 'amex') {
    if (cleanCvc.length !== 4) {
      return {
        isValid: false,
        error: 'American Express security code must be 4 digits',
      }
    }
  } else {
    if (cleanCvc.length !== 3) {
      return { isValid: false, error: 'Security code must be 3 digits' }
    }
  }

  return { isValid: true }
}

/**
 * Validate cardholder name
 */
export const validateCardholderName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Cardholder name is required' }
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Cardholder name is too short' }
  }

  if (name.length > 50) {
    return { isValid: false, error: 'Cardholder name is too long' }
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s\-'.]+$/.test(name)) {
    return { isValid: false, error: 'Invalid characters in cardholder name' }
  }

  return { isValid: true }
}

/**
 * Luhn algorithm implementation for card validation
 */
const luhnCheck = (cardNumber: string): boolean => {
  let sum = 0
  let shouldDouble = false

  // Loop through values starting from the rightmost digit
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i), 10)

    if (shouldDouble) {
      digit *= 2
      if (digit > 9) {
        digit = (digit % 10) + 1
      }
    }

    sum += digit
    shouldDouble = !shouldDouble
  }

  return sum % 10 === 0
}

/**
 * Get card type from number
 */
const getCardType = (number: string): string => {
  const patterns: Record<string, RegExp[]> = {
    visa: [/^4/],
    mastercard: [/^5[1-5]/, /^2[2-7]/],
    amex: [/^3[47]/],
    discover: [/^6(?:011|5)/],
    jcb: [/^35/],
    diners: [/^3[068]/],
  }

  for (const [type, regexes] of Object.entries(patterns)) {
    if (regexes.some(regex => regex.test(number))) {
      return type
    }
  }

  return 'unknown'
}

/**
 * Validate card length based on card type
 */
const validateCardTypeLength = (number: string, cardType: string): boolean => {
  const lengthMap: Record<string, number[]> = {
    visa: [13, 16, 19],
    mastercard: [16],
    amex: [15],
    discover: [16],
    jcb: [16],
    diners: [14],
  }

  const validLengths = lengthMap[cardType] || [13, 14, 15, 16, 17, 18, 19]
  return validLengths.includes(number.length)
}

/**
 * Validate complete payment form
 */
export interface PaymentFormData {
  cardNumber: string
  expiry: string
  cvc: string
  cardholderName?: string
}

export const validatePaymentForm = (data: PaymentFormData): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {}

  const cardNumberResult = validateCardNumber(data.cardNumber)
  if (!cardNumberResult.isValid) {
    errors.cardNumber = cardNumberResult.error!
  }

  const expiryResult = validateCardExpiry(data.expiry)
  if (!expiryResult.isValid) {
    errors.expiry = expiryResult.error!
  }

  const cvcResult = validateCardCvc(data.cvc, getCardType(data.cardNumber.replace(/\s/g, '')))
  if (!cvcResult.isValid) {
    errors.cvc = cvcResult.error!
  }

  if (data.cardholderName) {
    const nameResult = validateCardholderName(data.cardholderName)
    if (!nameResult.isValid) {
      errors.cardholderName = nameResult.error!
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}
